import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NormaliseNavData,
  normaliseNavDataAsync,
} from '@/modules/issuer-dashboard/helpers/normalizeNavData.ts';
import { useIssuedCapitalOverTimeQuery } from '@/modules/issuer-dashboard/queries/securities.ts';
import { BalanceChartCanvas, ChartEmptyState, FilterItem, InfoCard, Spinner } from 'ui';
import { GetRegisterClassesWithNAVResponse, RegisterClassWithNAVResponse } from 'api';

dayjs.extend(utc);

interface IssuedCapitalOverTimeChartProps {
  register_class_ids?: number[];
  fullHeight?: boolean;
}
const chartTickAmount = 6;

export const IssuedCapitalOverTimeChart = ({
  register_class_ids,
  fullHeight = true,
}: IssuedCapitalOverTimeChartProps) => {
  const [filters, setFilters] = useState<{ date_from?: string; date_to?: string }>({});

  const dateValue = useMemo(
    () =>
      filters.date_from && filters.date_to
        ? ([dayjs(filters.date_from), dayjs(filters.date_to)] as [Dayjs, Dayjs])
        : undefined,
    [filters.date_from, filters.date_to],
  );

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              date_from: date[0].toISOString(),
              date_to: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['date_from', 'date_to']);
      });
    },
    [setFilters],
  );

  const {
    data: dataIssuedCapitalOverTime,
    isLoading,
    isFetching,
    isError,
  } = useIssuedCapitalOverTimeQuery({
    ...filters,
    // register_class_ids: register_class_ids?.join(','),
  });
  const [chartData, setChartData] = useState<NormaliseNavData[]>([]);
  const [isNormalizing, setIsNormalizing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsNormalizing(true);

    const newDate = dataIssuedCapitalOverTime?.data?.map<RegisterClassWithNAVResponse>(
      item =>
        ({
          id: item.security.id,
          class_name: item.security.class_name,
          navs: item.data?.map(nItem => ({
            changes_date: format(new Date(nItem.date), 'yyyy-MM-dd'),
            price_per_share: nItem.value,
          })),
        } as RegisterClassWithNAVResponse),
    );

    void normaliseNavDataAsync(
      {
        data: newDate,
      } as GetRegisterClassesWithNAVResponse,
      !dateValue?.[1],
    )
      .then(result => {
        if (cancelled) return;

        setChartData(
          result.filter(item => register_class_ids?.includes(item.id) || !register_class_ids),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setChartData([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsNormalizing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dataIssuedCapitalOverTime?.data, dateValue, register_class_ids]);

  return (
    <InfoCard
      title="Issued capital over time"
      classes={{
        block: `${fullHeight ? 'h-full ' : ''}min-h-[350px]`,
        content: fullHeight
          ? 'flex flex-1 flex-col w-full min-h-[350px] p-0 px-6'
          : 'w-full p-0 px-6',
      }}
      headerAction={
        <div className="flex items-center justify-end gap-2">
          {isFetching && !isLoading && <Spinner size="md" />}
          <FilterItem
            placeholder="Date"
            type="date"
            onChange={handleChangeDate}
            value={dateValue}
            className="rounded-lg"
          />
        </div>
      }
    >
      <div
        className={
          fullHeight ? 'flex min-h-[350px] flex-1 flex-col gap-4' : 'flex h-[350px] flex-col gap-4'
        }
      >
        {(isLoading || isNormalizing) && !chartData.length && (
          <div className="flex flex-col w-full h-[200px] items-center justify-center">
            <Spinner size="lg" />
          </div>
        )}
        {!isLoading && !isNormalizing && !chartData?.length && (
          <div className="flex flex-1 items-center justify-center py-4 text-lg font-medium">
            <ChartEmptyState isError={isError} isLoading={isLoading} />
          </div>
        )}
        {!isLoading && !isNormalizing && chartData?.length > 0 && (
          <div className={fullHeight ? 'min-h-[350px] flex-1' : 'h-[350px]'}>
            <BalanceChartCanvas
              height="100%"
              fill={false}
              type="line"
              data={chartData}
              colors={['#1E6091', '#48CAE4', '#56C596', '#F4C430', '#B276E2']}
              countsLabel={chartTickAmount}
              markerStrokeColor="#FFFFFF"
            />
          </div>
        )}
      </div>
    </InfoCard>
  );
};
